import type {ReactElement} from 'react';
import React, {useMemo} from 'react';
import {AddTextDropdownOptions} from '@Components/poster-editor/components/poster-add-item-bar/components/add-text-dropdown-options';
import {AddLayoutsDropdownOptions} from '@Components/poster-editor/components/poster-add-item-bar/components/add-layouts-dropdown-options';
import {AddBackgroundDropdownOptions} from '@Components/poster-editor/components/poster-add-item-bar/components/add-background-dropdown-options';
import useWindowSize from '@Hooks/useWindowSize';
import {onOpenStockMediaModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {AddItemButton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-button';
import {RecordDropdownOptions} from '@Components/poster-editor/components/poster-add-item-bar/components/record-dropdown-options';
import {AddItemSkeleton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-skeleton';
import {AddUserMediaButton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-user-media-button';
import {PosterAddItemBarHeightThreshold} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';
import {ElementTestId} from '@Tests/tests.types';
import {AddAiItemButton} from '@Components/add-ai-item-button/add-ai-item-button';
import {AddItemBarErrorBoundary} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-bar-error-boundary/add-item-bar-error-boundary';
import {TemplatesDropdown} from '@Components/poster-editor/components/poster-add-item-bar/components/templates-dropdown/templates-dropdown';
import styles from './poster-add-item-bar.module.scss';
import {useAppSelector} from '@/hooks';
import {SlideshowDropdown} from '@Components/poster-editor/components/poster-add-item-bar/components/slideshow-dropdown/slideshow-dropdown';
import {addQrCodeToPage} from '@PosterWhiteboard/items/qr-code-item.class';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';
import {DrawOption} from '@Components/poster-editor/components/poster-add-item-bar/components/draw-option/draw-option';
import {PosterEditorToolbar} from '@Components/poster-editor-toolbar/poster-editor-toolbar';
import {ADD_ITEM_BAR_ITEM_GAP, ADD_ITEM_BAR_ITEM_HEIGHT} from '@Components/poster-editor-toolbar/poster-editor-toolbar.types';
import {isTemplatesPopupHidden} from '@Components/poster-editor/library/poster-editor-library';

const SKELETON_VALUES = 5;

export function PosterAddItemBar(): ReactElement<any> {
  const {windowHeight} = useWindowSize();
  const showSkeletons = useShowContextualSkeletons();
  const bottomBarHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.bottomBarHeight ?? 60;
  });
  const isTimelineModeActive = useAppSelector((state) => {
    return state.posterEditor.isTimelineModeActive;
  });
  const isPickingModeEnabled = useAppSelector((state) => {
    return state.posterEditor.isPickingModeEnabled;
  });
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });
  const BOTTOM_BAR_BOTTOM_OFFSET = 24;

  const BUFFER = 80;

  const TEMPLATES_BUFFER = isTemplatesPopupHidden() ? -70 : 0;

  const OFFSET = (isTimelineModeActive ? bottomBarHeight + BOTTOM_BAR_BOTTOM_OFFSET * 2 : 60) + BUFFER + TEMPLATES_BUFFER;

  const isMinimumHeight = (): boolean => {
    return windowHeight < 185 + OFFSET;
  };

  const isXXXSmallHeight = (): boolean => {
    return windowHeight < 290 + OFFSET;
  };

  const isXXSmallHeight = (): boolean => {
    return windowHeight < 390 + OFFSET;
  };

  const isXSmallHeight = (): boolean => {
    return windowHeight < 480 + OFFSET;
  };

  const isSmallHeight = (): boolean => {
    return windowHeight < 575 + OFFSET;
  };

  const isMediumHeight = (): boolean => {
    return windowHeight < 665 + OFFSET;
  };

  const isLargeHeight = (): boolean => {
    return windowHeight < 745 + OFFSET;
  };

  const isXLargeHeight = (): boolean => {
    return windowHeight < 830 + OFFSET;
  };

  const isXXLargeHeight = (): boolean => {
    return windowHeight < 950 + OFFSET;
  };

  const getSkeleton = (index: number): ReactElement<any> => {
    return <AddItemSkeleton key={index} />;
  };

  const prepareSkeletons = (): ReactElement<any>[] => {
    const items: ReactElement<any>[] = [];

    for (let i = 0; i < SKELETON_VALUES; i++) {
      items.push(getSkeleton(i));
    }
    return items;
  };

  const getHeightThreshold = (): PosterAddItemBarHeightThreshold => {
    if (isMinimumHeight()) {
      return PosterAddItemBarHeightThreshold.MINIMUM;
    }
    if (isXXXSmallHeight()) {
      return PosterAddItemBarHeightThreshold.XXXSMALL;
    }
    if (isXXSmallHeight()) {
      return PosterAddItemBarHeightThreshold.XXSMALL;
    }

    if (isXSmallHeight()) {
      return PosterAddItemBarHeightThreshold.XSMALL;
    }

    if (isSmallHeight()) {
      return PosterAddItemBarHeightThreshold.SMALL;
    }

    if (isMediumHeight()) {
      return PosterAddItemBarHeightThreshold.MEDIUM;
    }

    if (isLargeHeight()) {
      return PosterAddItemBarHeightThreshold.LARGE;
    }

    if (isXLargeHeight()) {
      return PosterAddItemBarHeightThreshold.XLARGE;
    }

    if (isXXLargeHeight()) {
      return PosterAddItemBarHeightThreshold.XXLARGE;
    }

    return PosterAddItemBarHeightThreshold.XXXLARGE;
  };

  const onAddQr = (): void => {
    addQrCodeToPage();
  };

  const getBarMaxHeight = (): number => {
    const itemCounts: Record<PosterAddItemBarHeightThreshold, number> = {
      [PosterAddItemBarHeightThreshold.MINIMUM]: 3,
      [PosterAddItemBarHeightThreshold.XXXSMALL]: 4,
      [PosterAddItemBarHeightThreshold.XXSMALL]: 5,
      [PosterAddItemBarHeightThreshold.XSMALL]: 6,
      [PosterAddItemBarHeightThreshold.SMALL]: 7,
      [PosterAddItemBarHeightThreshold.MEDIUM]: 8,
      [PosterAddItemBarHeightThreshold.LARGE]: 9,
      [PosterAddItemBarHeightThreshold.XLARGE]: 10,
      [PosterAddItemBarHeightThreshold.XXLARGE]: 11,
      [PosterAddItemBarHeightThreshold.XXXLARGE]: 11,
    };
    const CONTAINER_VERTICAL_PADDING = 16;
    const OFFSET = 7;
    const TOTAL_ITEMS = isTemplatesPopupHidden() ? 10 : 11;
    const count = itemCounts[getHeightThreshold()];

    if (count < TOTAL_ITEMS) {
      return (count - 1) * ADD_ITEM_BAR_ITEM_HEIGHT + (count - 1) * ADD_ITEM_BAR_ITEM_GAP + ADD_ITEM_BAR_ITEM_HEIGHT / 2 + CONTAINER_VERTICAL_PADDING + OFFSET;
    }

    return count * ADD_ITEM_BAR_ITEM_HEIGHT + (count - 1) * ADD_ITEM_BAR_ITEM_GAP + CONTAINER_VERTICAL_PADDING;
  };

  const getToolbarOptions = (): ReactElement<any>[] => {
    if (showSkeletons) {
      return prepareSkeletons();
    }

    return [
      <AddUserMediaButton id="toolbar-media-onboarding" key="toolbar-media-onboarding" data-testid={ElementTestId.ADD_ITEM_BAR_MY_UPLOADS} />,
      <TemplatesDropdown key="toolbar-templates" arrowClasses={styles.arrow} popupClassName={styles.templatesPopupPanel} />,
      <AddItemButton
        onClick={onOpenStockMediaModal}
        key="toolbar-uploads"
        text={window.i18next.t('pmwjs_media')}
        icon="icon-media"
        data-testid={ElementTestId.ADD_ITEM_BAR_MEDIA}
      />,
      <AddTextDropdownOptions key="toolbar-text" popupClassName={styles.toolbarPanel} arrowClasses={styles.arrow} data-testid={ElementTestId.ADD_ITEM_BAR_TEXT} />,
      <AddAiItemButton key="add-ai-item" arrowClasses={styles.arrow} isSmallHeight={false} data-testid={ElementTestId.ADD_ITEM_BAR_AI} />,
      <AddBackgroundDropdownOptions
        isHidden={false}
        key="toolbar-background"
        popupClassName={styles.toolbarPanel}
        arrowClasses={styles.arrow}
        data-testid={ElementTestId.ADD_ITEM_BAR_BACKGROUND}
      />,
      <AddLayoutsDropdownOptions
        isHidden={false}
        key="toolbar-layouts"
        popupClassName={styles.toolbarPanel}
        arrowClasses={styles.arrow}
        data-testid={ElementTestId.ADD_ITEM_BAR_LAYOUT}
      />,
      <RecordDropdownOptions
        isHidden={false}
        key="toolbar-record"
        popupClassName={styles.toolbarPanel}
        arrowClasses={styles.arrow}
        data-testid={ElementTestId.ADD_ITEM_BAR_RECORD}
      />,
      <DrawOption key="toolbar-draw" showDrawOption={true} />,
      <SlideshowDropdown isHidden={false} key="toolbar-slideshow" popupClassName={styles.toolbarPanel} arrowClasses={styles.arrow} />,
      <AddItemButton hasMarginBottom={false} id="toolbar-qr-code" key="toolbar-qr-code" onClick={onAddQr} text={window.i18next.t('pmwjs_qr_code')} icon="icon-qr-content" />,
    ];
  };

  return useMemo((): ReactElement<any> => {
    return (
      <AddItemBarErrorBoundary fallback={<div className="flex flex-v-row spacing-p-3 _full-height">{prepareSkeletons()}</div>}>
        <PosterEditorToolbar
          className={`${styles.toolbar} ${isPickingModeEnabled ? styles.pickingMode : ''} ${isVersionHistoryModeActive ? '_hidden' : ''}`}
          maxHeight={getBarMaxHeight()}
          items={getToolbarOptions()}
        />
      </AddItemBarErrorBoundary>
    );
  }, [showSkeletons, windowHeight, bottomBarHeight, OFFSET, isPickingModeEnabled, isVersionHistoryModeActive]);
}
